// src/TransparenciaIA.jsx
//
// Bloco 3 (Resolução CFM 2.454/2026 — Anexo III, item I; Art. 11):
// página pública de transparência de IA. Acessível em /transparencia-ia
// sem login. Linkada pelo AiAssistedBadge nas mensagens do chat e
// referenciada nos Termos de Uso (cláusula 9).
//
// Design harmonizado com o shell LegalPageShell (mesmo padrão de
// /termos-de-uso e demais páginas institucionais — Cormorant Garamond
// na headline, paleta da marca, eyebrow art-déco).

import React from 'react';
import LegalPageShell from './components/LegalPageShell';
import PageSeo from './seo/PageSeo';

const ENV_VERSION = process.env.REACT_APP_VERSION || '1.0';

const TransparenciaIA = () => (
  <>
  <PageSeo
    path="/transparencia-ia"
    title="Transparência sobre o uso de IA — Consulta+ (CFM 2.454/2026)"
    description="Documento público de transparência da Consulta+ sobre o uso de Inteligência Artificial nos atendimentos médicos, em conformidade com a Resolução CFM 2.454/2026 (Anexo III, item I; Art. 11). Onde a IA é usada, supervisão humana e direitos do paciente."
    breadcrumbs={[
      { name: 'Início', url: '/' },
      { name: 'Transparência IA', url: '/transparencia-ia' },
    ]}
  />
  <LegalPageShell
    eyebrow="Transparência IA · CFM 2.454/2026"
    title="Transparência sobre o uso de Inteligência Artificial"
    subtitle={`Documento público em conformidade com a Resolução CFM nº 2.454/2026 (Anexo III, item I; Art. 11) · Versão ${ENV_VERSION} — atualizada em 18/10/2026.`}
    contactEmail="atendimento@consultamais.org"
    contactPrefix="Para esclarecimentos, contestações ou solicitações de auditoria, escreva para"
    heroImage="/img/heros/hero-transparencia-ia-16x9.webp"
    heroAlt="Pintura modernista de uma médica segurando uma lanterna ao lado de uma figura estilizada de inteligência artificial"
  >
    <h2>1. Quem somos</h2>
    <p>
      O <strong>Consulta+</strong> (consultamais.app) é uma plataforma de teleconsulta médica
      assíncrona por chat, regida pela legislação brasileira aplicável à telemedicina, ao Código de
      Ética Médica (Resolução CFM 2.217/2018) e à proteção de dados pessoais (LGPD — Lei 13.709/2018).
    </p>
    <ul>
      <li>
        <strong>Diretor Técnico responsável pela IA:</strong> Dr. Guilherme Esmanhotto — CRM
        <strong> 23.706/DF</strong>.
      </li>
      <li>
        <strong>Comissão de IA e Telemedicina</strong> sob coordenação médica e subordinada à
        diretoria técnica (Resolução CFM 2.454/2026, Art. 14 §único; modelo expansível).
      </li>
      <li>
        <strong>Contato institucional (Diretor Técnico / Encarregado):</strong>{' '}
        <a href="mailto:atendimento@consultamais.org">atendimento@consultamais.org</a>.
      </li>
    </ul>

    <h2>2. Onde a IA é usada no atendimento</h2>
    <p>
      Empregamos Inteligência Artificial como <strong>ferramenta de apoio</strong> em pontos
      específicos do fluxo, sempre sob supervisão humana obrigatória. A IA <em>não substitui</em> a
      decisão final do médico humano.
    </p>
    <ul>
      <li>
        <strong>Triagem inicial (“Alice”).</strong> Conduz a anamnese estruturada antes da
        consulta, organizando sintomas, histórico, alergias e medicações.
      </li>
      <li>
        <strong>Perguntas adicionais.</strong> Pode formular perguntas complementares para
        aprofundar o quadro clínico antes da decisão médica.
      </li>
      <li>
        <strong>Conduta inicial e sugestões clínicas.</strong> Elabora rascunhos de orientações,
        receitas, atestados, encaminhamentos e pedidos de exame. Quando houver consultas anteriores
        na plataforma, o rascunho pode considerar resumos clínicos anonimizados desses atendimentos
        (sem identificação pessoal — ver seção 3). As saídas ocorrem com supervisão médica.
      </li>
      <li>
        <strong>Interpretação clínica de imagem ou exames anexados.</strong> Fotos clínicas
        (garganta, olho, lesão de pele) e laudos em PDF podem ser analisados por IA; o resultado é
        apenas apoio ao médico.
      </li>
      <li>
        <strong>Sofia (suporte operacional).</strong> Chatbot de FAQ pré e pós-consulta. Não tem
        acesso a prontuário e <strong>transfere automaticamente para a equipe humana</strong> em
        qualquer questão clínica.
      </li>
      <li>
        <strong>Detecção de alertas clínicos.</strong> Camada de regras determinísticas e apoio LLM
        para identificar sinais de gravidade (gestante com pré-eclâmpsia, doenças
        infectocontagiosas, ideação suicida).
      </li>
    </ul>

    <h2>3. Modelos utilizados</h2>
    <p>
      Os modelos abaixo refletem a configuração de produção em 10/08/2026.
    </p>
    <ul>
      <li>
        <strong>OpenAI GPT-5.6 (Luna)</strong> — modelo para a triagem (Alice), a conduta clínica,
        os encaminhamentos (incluindo o rascunho antecipado), a sugestão de receita, a IA
        personalizada do médico, o rascunho antecipado de pedido de exames, a Sofia (suporte),
        os resumos de continuidade clínica entre consultas, a interpretação de imagens clínicas e
        PDFs de laudo, e o fallback automático após limite de taxa dos demais modelos. Adotado
        após experimentos comparativos internos sobre atendimentos reais, com julgamento cego,
        validação estatística (intervalo de confiança de 95% / não-inferioridade) e revisão médica
        dos casos sensíveis — o modelo anterior foi mantido nas tarefas em que o novo não
        demonstrou empate ou superioridade com segurança.
      </li>
      <li>
        <strong>OpenAI GPT-5.2</strong> — modelo para o prontuário e a coleta clínica, a validação
        farmacológica de receita, as sugestões de atestado, as sugestões de exames fora do rascunho
        antecipado e as perguntas adicionais. Também serve de fallback se a Luna estiver sob
        limite de taxa.
      </li>
    </ul>
    <p>
      Conforme política padrão da API da OpenAI desde março de 2023, os dados do paciente{' '}
      <strong>não são utilizados para treinamento</strong> de modelos. Adicionalmente, todas as
      nossas chamadas usam o parâmetro <code>store: false</code>, eliminando a retenção opcional
      de application state. Por padrão da OpenAI, inputs e outputs ainda podem ser retidos por até{' '}
      <strong>30 dias</strong> para fins de monitoramento contra abusos, sem acesso humano em
      condições normais.
    </p>
    <p>
      <strong>Não enviamos dados de identificação do paciente para o modelo de IA.</strong> Em
      particular, ficam fora do prompt enviado à OpenAI:
    </p>
    <ul>
      <li>nome completo, CPF, e-mail e telefone do cadastro;</li>
      <li>identificadores internos do paciente (<code>userId</code>) e do canal de chat;</li>
      <li>quaisquer parâmetros opcionais de rastreamento por usuário oferecidos pela API
        (<code>user</code>, <code>safety_identifier</code>) — não enviamos nenhum deles.</li>
    </ul>
    <p>
      O modelo recebe apenas o conteúdo clínico necessário para gerar conduta ou sugestões
      (sintomas, idade, sexo biológico, alergias, medicações em uso, queixa principal, histórico
      clínico da consulta em curso e — quando o caso exigir — peso/altura/IMC e imagens clínicas
      anexadas pelo paciente). Esses são os dados sensíveis indispensáveis ao atendimento,
      tratados sob a base legal de cuidados de saúde da LGPD (art. 11, II, alínea “f”).
    </p>
    <p>
      <strong>Continuidade entre consultas (histórico clínico anonimizado).</strong> Quando há
      atendimentos anteriores do mesmo paciente na plataforma, podemos incluir no contexto interno
      da IA — no rascunho de conduta e em sugestões clínicas — resumos curtos dessas consultas
      (queixa, hipótese, conduta, documentos emitidos e alertas relevantes, como alergias). O
      objetivo é apoiar a continuidade do cuidado e o raciocínio sobre recorrência, sob supervisão
      médica. Esse pareamento ocorre <strong>somente nos nossos sistemas</strong>; o
      modelo de IA <strong>não recebe nome, CPF, e-mail, telefone, userId nem identificador de
      canal</strong>, e <strong>não tem vínculo com o cadastro do paciente</strong>. Em outras
      palavras: a IA permanece sem saber quem é a pessoa atendida — recebe apenas texto clínico
      anonimizado, sem identificação pessoal. Os mesmos resumos podem ser exibidos ao médico no
      painel de atendimento; não são citados ao paciente como registro de consultas anteriores.
    </p>
    <p style={{ fontSize: '0.92rem', color: '#374151' }}>
      Limite inerente: como a triagem é por chat, o paciente pode espontaneamente digitar dados
      pessoais nas mensagens (ex.: o próprio nome) ou enviar foto de documento de identidade. Esse
      conteúdo integra o contexto enviado ao modelo, inclusive quando a imagem é interpretada pela
      IA. Não há filtragem automática para preservar a fidelidade clínica do registro.
    </p>

    <h2>4. Classificação de risco (Anexo II da Resolução)</h2>
    <p>Avaliamos cada componente conforme o Anexo II:</p>
    <ul>
      <li>
        <strong>Sofia (suporte operacional)</strong>: <strong>baixo risco</strong> — funções
        administrativas, sem influência decisória clínica.
      </li>
      <li>
        <strong>Alice / triagem, conduta IA, sugestões clínicas, interpretação de imagem,
        alertas</strong>: <strong>médio risco</strong> — apoiam decisões clínicas, mas não as
        executam de forma autônoma. A supervisão humana obrigatória é o controle principal.
      </li>
      <li>
        <strong>Nenhum componente é classificado como alto ou inaceitável</strong>, dado que a
        teleconsulta é assíncrona e por médico humano, sem automação de decisões críticas
        irreversíveis.
      </li>
    </ul>
    <p>
      O documento técnico completo é a <strong>Avaliação Preliminar de Risco</strong> mantida pelo
      Diretor Técnico, disponibilizada a órgãos de controle (CFM, CRM-DF, ANPD, MP) mediante
      solicitação formal.
    </p>

    <h2>5. Mecanismos de supervisão e controle de qualidade</h2>
    <ul>
      <li>
        <strong>Supervisão humana obrigatória.</strong> Toda conduta gerada por IA fica sob
        supervisão do médico no painel de atendimento.
      </li>
      <li>
        <strong>Validação farmacológica.</strong> Receitas geradas com apoio de IA passam por
        verificação de interações, posologia e contraindicações com base na ANVISA antes da
        emissão.
      </li>
      <li>
        <strong>Camadas determinísticas.</strong> Regras codificadas (não-LLM) garantem pisos
        clínicos para doenças infectocontagiosas, atestados de aptidão física, prescrição
        pediátrica e proteção de gestantes.
      </li>
      <li>
        <strong>Auditorias regulares.</strong> Monitoramento por scripts de auditoria, com análise
        estratificada por idade, sexo e queixa principal.
      </li>
      <li>
        <strong>Trilha de auditoria por atendimento.</strong> Cada mensagem clínica gerada com
        apoio de IA registra rastreabilidade (rascunho original + texto enviado pelo médico + flag
        de edição) por até <strong>20 anos</strong>, em conformidade com a guarda de prontuário
        (Lei 13.787/2018).
      </li>
    </ul>

    <h2>6. Limitações conhecidas</h2>
    <ul>
      <li>
        <strong>Teleconsulta assíncrona não substitui pronto-atendimento presencial.</strong> Em
        caso de sintomas graves ou risco iminente, procure imediatamente um pronto-socorro.
      </li>
      <li>
        <strong>A IA pode cometer erros de interpretação ou omissões na triagem.</strong> Por isso,
        antes do atendimento médico, você é convidado a revisar todas as informações coletadas e
        corrigir o que estiver incorreto pelo próprio chat (ver cláusula 9.5 dos Termos de Uso).
      </li>
      <li>
        <strong>Sem exame físico presencial,</strong> determinadas condutas (afastamento
        prolongado, certas prescrições, atestado de aptidão física) podem não ser possíveis e o
        médico poderá orientar avaliação presencial.
      </li>
      <li>
        <strong>O resultado da IA não é definitivo.</strong> Você sempre tem direito a buscar
        segunda opinião (Resolução 2.454/2026, Art. 10, II).
      </li>
    </ul>

    <h2>7. Seus direitos quanto à IA</h2>
    <ul>
      <li>
        <strong>Ser informado</strong> de forma clara e acessível quando a IA é usada como apoio
        relevante no seu cuidado (Art. 5°§1° e Art. 11) — esta página atende a esse direito.
      </li>
      <li>
        <strong>Receber explicação</strong> compreensível sobre como uma sugestão de IA influenciou
        sua conduta (Princípio II da Exposição de Motivos).
      </li>
      <li>
        <strong>Supervisão humana obrigatória</strong> de toda decisão derivada de IA (Anexo I, XX —
        contestabilidade).
      </li>
      <li>
        <strong>Solicitar segunda opinião</strong> a qualquer momento (Art. 10, II).
      </li>
      <li>
        <strong>Direitos LGPD</strong> sobre seus dados pessoais e de saúde (acesso, correção,
        eliminação observada a guarda do prontuário). Veja a{' '}
        <a href="/lgpd">Política de Privacidade</a>.
      </li>
    </ul>

    <h2>8. Canal de contestabilidade</h2>
    <p>
      Se você quiser <strong>contestar uma conduta apoiada por IA</strong> ou pedir revisão de
      qualquer resultado:
    </p>
    <ul>
      <li>
        <strong>E-mail (Diretor Técnico / Encarregado):</strong>{' '}
        <a href="mailto:atendimento@consultamais.org?subject=%5BCONTESTABILIDADE-IA%5D">
          atendimento@consultamais.org
        </a>{' '}
        com assunto <code>[CONTESTABILIDADE-IA]</code>.
      </li>
      <li>
        <strong>Chat médico</strong> (durante a consulta ativa): comunique imediatamente o médico
        responsável.
      </li>
      <li>
        <strong>SLA de resposta:</strong> até 15 dias corridos a partir da abertura (LGPD art. 19
        §1°).
      </li>
    </ul>
    <p>
      Cada contestação gera registro interno auditável e é submetida ao Diretor Técnico. Quando
      procedente, a conduta é revisada e a causa-raiz tratada (ajuste de prompt, regra adicional ou
      descontinuação do componente, conforme o caso).
    </p>

    <h2>9. Acesso de órgãos de controle</h2>
    <p>
      Garantimos cooperação plena com{' '}
      <strong>CFM, CRM-DF, Ministério Público, ANPD, CONEP e Procon</strong> e demais autoridades
      competentes, observados o segredo médico, a proteção de dados pessoais e os segredos
      industriais aplicáveis.       Solicitações formais podem ser enviadas para{' '}
      <a href="mailto:atendimento@consultamais.org?subject=%5B%C3%93RG%C3%83O-CONTROLE%5D">
        atendimento@consultamais.org
      </a>{' '}
      com assunto <code>[ÓRGÃO-CONTROLE]</code>.
    </p>

    <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
      Documentos técnicos completos disponíveis a órgãos de controle, mediante solicitação formal:
      Avaliação Preliminar de Risco,
      Avaliação de Impacto Algorítmico (AIA), Política de Monitoramento de Viés e Qualidade,
      Runbook de Descontinuação de Modelo, Plano de Proteção de Dados aplicado à IA, Ato de
      Nomeação do Diretor Técnico e Comissão de IA e Telemedicina.
    </p>
  </LegalPageShell>
  </>
);

export default TransparenciaIA;
